import { colors } from '../../theme/colors';
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '../common/HoverTooltip';
import { CopyToClipboard } from '../common/ProjectIcons';
import { copyToClipboard } from '../common/copyToClipboard';
import Editor from '@monaco-editor/react';
import { MONO_FONT_FAMILY } from '../../fonts';
import type { editor } from 'monaco-editor';
import { inputClasses, buttonClasses } from '../common/tailwindClassnames';
import { SegmentedToggle } from '../common/SegmentedToggle';
import { GUNDI_OBJECT_SPECS } from './schema-builder/gundiObjectSpecs';
import { validateMappedOutput } from './schema-builder/validation';
import { SelectMenu } from './schema-builder/SelectMenu';
import { registerJqLanguage } from './jqLanguage';
import {
  CoreFieldMaps,
  AdditionalField,
  Condition,
  OPS,
  ParseResult,
  Transform,
  TransformMap,
} from './jq-filter-builder/types';
import {
  jqPath,
  valAt,
  flatPaths,
  genJq,
  evalMapping,
  parseJqToBuilderState,
  applyTransformsJs,
  formatPreviewText,
  normalizeTransforms,
} from './jq-filter-builder/helpers';
import {
  TransformAddButton,
  TransformPills,
} from './jq-filter-builder/TransformControls';
import {
  PANEL_CLASSES,
  HEADER_CLASSES,
  SECTION_LABEL,
  MAPPING_DISPLAY,
  MAPPING_DISPLAY_WARNING,
  DROP_ZONE,
  DROP_ZONE_ACTIVE,
  DROP_ZONE_IDLE,
  DROP_ZONE_TARGET_HOT,
} from './jq-filter-builder/styles';

// ── Drag-and-drop helpers ──

type DragPathRef = React.MutableRefObject<string[] | null>;

// Keyboard/click-mapping handshake: a drop slot calls this on Enter or click to hand its
// "pick" callback to the parent, which then moves focus into the source tree. Selecting a
// leaf (Enter or click) invokes the callback with that leaf's path. Mirrors the
// drag-and-drop flow for keyboards and for slots scrolled out of drag reach.
type StartPick = (
  onPick: (path: string[]) => void,
  slotEl: HTMLElement,
) => void;

// Applied imperatively to the armed slot while a pick is in progress (the slot's own
// render doesn't know it is the pending one — the pick state lives on the parent).
const ARMED_SLOT_CLASSES = ['!border-ui-cta-primary', 'animate-pulse'];

// The Code editor's floor, and the height the panel has always had. It grows
// from here to whatever the filter needs, so a short filter looks the same as
// before and a long one is never scrolled inside its own box.
const CODE_EDITOR_MIN_HEIGHT = 360;

function useDragOver(readOnly: boolean) {
  const [isOver, setIsOver] = useState(false);

  const handlers = readOnly
    ? {}
    : {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setIsOver(true);
        },
        onDragLeave: () => setIsOver(false),
      };

  const resetOver = () => setIsOver(false);

  return { isOver, handlers, resetOver };
}

// ── Sub-components ──

function TreeNode({
  name,
  value,
  path,
  depth,
  collapsed,
  onToggle,
  readOnly,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPathKey,
}: {
  name: string;
  value: any;
  path: string[];
  depth: number;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onLeafActivate?: (path: string[]) => void;
  pickActive?: boolean;
  // Path key of the leaf armed for reverse (source-first) click-mapping, if any.
  armedPathKey?: string;
}) {
  const { t } = useTranslation();
  const isObject =
    value != null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isLeaf = !isObject && !isArray;
  const pathKey = path.join('.');
  const hasChildren = isObject
    ? Object.keys(value as object).length > 0
    : isArray
      ? (value as any[]).length > 0
      : false;

  const typeColor =
    typeof value === 'number'
      ? colors['unlisted-json-number-blue']
      : typeof value === 'string'
        ? colors['ui-cta-primary']
        : typeof value === 'boolean'
          ? colors['unlisted-orange']
          : colors['unlisted-medium-gray'];

  const displayValue = isObject
    ? `{${Object.keys(value as object).length}}`
    : isArray
      ? `[${(value as any[]).length}]`
      : JSON.stringify(value);

  const ghostRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = (e: React.DragEvent) => {
    if (!isLeaf || readOnly) return;
    dragPathRef.current = path;
    e.dataTransfer.setData('text/plain', pathKey);
    e.dataTransfer.effectAllowed = 'copy';

    const ghost = document.createElement('div');
    ghost.textContent = jqPath(path);
    ghost.style.cssText = `position:absolute;top:-100px;left:-100px;padding:4px 10px;background:${colors['ui-cta-primary']};color:${colors['white']};border-radius:6px;font:12px ${MONO_FONT_FAMILY};white-space:nowrap;`;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 0, 0);
  };

  const handleDragEnd = () => {
    dragPathRef.current = null;
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      ghostRef.current?.remove();
    };
  }, []);

  return (
    <div>
      <div
        draggable={isLeaf && !readOnly}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        tabIndex={isLeaf && !readOnly ? (pickActive ? 0 : -1) : undefined}
        role={isLeaf && !readOnly ? 'button' : undefined}
        aria-label={isLeaf && !readOnly ? jqPath(path) : undefined}
        data-leaf-focusable={isLeaf && !readOnly ? 'true' : undefined}
        onKeyDown={
          isLeaf && !readOnly
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLeafActivate?.(path);
                }
              }
            : undefined
        }
        onClick={isLeaf && !readOnly ? () => onLeafActivate?.(path) : undefined}
        className={`flex select-none items-center gap-1 rounded px-1.5 py-0.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ui-cta-primary ${isLeaf && !readOnly ? `${pickActive ? 'cursor-pointer' : 'cursor-grab'} hover:bg-unlisted-light-blue` : 'cursor-default'} ${armedPathKey === pathKey ? 'bg-unlisted-light-blue ring-2 ring-inset ring-ui-cta-primary' : ''}`}
        style={{ paddingLeft: depth * 16 + 6 }}
      >
        {hasChildren ? (
          <span
            onClick={() => onToggle(pathKey)}
            className="w-3.5 cursor-pointer text-center text-[10px] text-ui-text-primary-text"
          >
            {collapsed.has(pathKey) ? '▶' : '▼'}
          </span>
        ) : (
          <span
            className="w-3.5 text-center text-[10px]"
            style={{ color: colors['unlisted-json-key-blue'] }}
          >
            ⠿
          </span>
        )}
        <span className="font-medium text-ui-text-primary-text">{name}</span>
        <span className="text-ui-text-primary-text">:</span>
        {isLeaf && (
          <span
            className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ color: typeColor }}
          >
            {displayValue}
          </span>
        )}
        {!isLeaf && (
          <span className="text-[11px] text-ui-text-primary-text">
            {displayValue}
          </span>
        )}
        {isLeaf && !readOnly && (
          <span className="ml-auto text-[10px] text-ui-text-primary-text">
            {t('drag →')}
          </span>
        )}
      </div>
      {isObject &&
        !collapsed.has(pathKey) &&
        Object.entries(value as Record<string, any>).map(
          ([childKey, childValue]) => (
            <TreeNode
              key={childKey}
              name={childKey}
              value={childValue}
              path={[...path, childKey]}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onLeafActivate={onLeafActivate}
              pickActive={pickActive}
              armedPathKey={armedPathKey}
            />
          ),
        )}
      {isArray &&
        !collapsed.has(pathKey) &&
        (value as any[]).map((item, index) => (
          <TreeNode
            key={index}
            name={`[${index}]`}
            value={item}
            path={[...path, String(index)]}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            readOnly={readOnly}
            dragPathRef={dragPathRef}
            onLeafActivate={onLeafActivate}
            pickActive={pickActive}
            armedPathKey={armedPathKey}
          />
        ))}
    </div>
  );
}

function DropSlot({
  label,
  mapping,
  sampleInput,
  onDrop,
  onClear,
  readOnly,
  dragPathRef,
  required,
  onStartPick,
  sourceArmed,
  transforms,
  onChangeTransforms,
  transformsLocked,
}: {
  label: string;
  mapping: string[] | null;
  sampleInput: any;
  onDrop: (path: string[]) => void;
  onClear: () => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  required?: boolean;
  onStartPick?: StartPick;
  // A source leaf is armed for click-mapping — highlight the empty zone as a hot target.
  sourceArmed?: boolean;
  transforms?: Transform[];
  onChangeTransforms?: (next: Transform[]) => void;
  // Transform edits regenerate jq; while the filter is complex the builder never pushes
  // regenerated jq to the parent, so editing here would silently not save.
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const previewValue = mapping ? valAt(sampleInput, mapping) : undefined;
  const pathNotInSample = mapping && previewValue === undefined;
  // Chip shows the RAW source value; the transformed result renders after the pills,
  // so the row reads before → after.
  const transformedValue = applyTransformsJs(
    previewValue,
    transforms,
    sampleInput,
  );
  const { t } = useTranslation();

  return (
    <div
      data-slot-row
      tabIndex={-1}
      className="flex items-start gap-2 overflow-hidden rounded border-b border-ui-divider-lines py-1.5 last:border-b-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ui-cta-primary"
    >
      <span
        className="flex shrink-0 items-center gap-1.5 pt-1 font-mono text-xs font-medium text-ui-text-primary-text"
        style={{ minWidth: 130 }}
      >
        {label}
        {required && (
          <HoverTooltip
            content={t('Required')}
            position="top"
            focusable={false}
            role="img"
            ariaLabel={t('Required')}
          >
            <span className="ml-0.5 font-bold text-unlisted-orange">*</span>
          </HoverTooltip>
        )}
      </span>
      <span className="shrink-0 pt-1 text-ui-text-primary-text">←</span>
      {mapping ? (
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Wand sits OUTSIDE the chip, always the last icon — same column as on
                        empty slots, so the icon layout is identical in both states. */}
          <div className="flex items-center gap-1">
            <div
              className={`${pathNotInSample ? MAPPING_DISPLAY_WARNING : MAPPING_DISPLAY} min-w-0 flex-1 overflow-hidden`}
            >
              <span
                className="min-w-0 flex-1 truncate"
                style={{ direction: 'rtl', textAlign: 'left' }}
                title={jqPath(mapping)}
              >
                <bdi>{jqPath(mapping)}</bdi>
              </span>
              {pathNotInSample ? (
                <span className="whitespace-nowrap text-[10px] italic">
                  {t('Not in schema')}
                </span>
              ) : (
                <span className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-ui-text-primary-text">
                  = {JSON.stringify(previewValue)}
                </span>
              )}
              {!readOnly && (
                <button
                  onClick={onClear}
                  className="px-0.5 text-base leading-none text-unlisted-orange hover:text-ui-status-error-destructive"
                >
                  ×
                </button>
              )}
            </div>
            {!readOnly && onChangeTransforms && (
              <TransformAddButton
                onAdd={(tr) => onChangeTransforms([...(transforms ?? []), tr])}
                disabled={transformsLocked}
              />
            )}
          </div>
          {transforms && transforms.length > 0 && onChangeTransforms && (
            <TransformPills
              transforms={transforms}
              onChange={onChangeTransforms}
              readOnly={readOnly || transformsLocked}
              previewText={formatPreviewText(transformedValue)}
            />
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-1">
            <div
              {...handlers}
              tabIndex={readOnly ? undefined : 0}
              role={readOnly ? undefined : 'button'}
              aria-label={
                readOnly
                  ? undefined
                  : sourceArmed
                    ? t('Click to map here')
                    : t('Drag a source field here')
              }
              onKeyDown={
                readOnly
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStartPick?.(onDrop, e.currentTarget);
                      }
                    }
              }
              onClick={
                readOnly
                  ? undefined
                  : (e) => onStartPick?.(onDrop, e.currentTarget)
              }
              onDrop={
                readOnly
                  ? undefined
                  : (e) => {
                      e.preventDefault();
                      resetOver();
                      if (dragPathRef.current) onDrop(dragPathRef.current);
                    }
              }
              className={`${DROP_ZONE} focus:outline-none focus:ring-2 focus:ring-ui-cta-primary ${isOver ? DROP_ZONE_ACTIVE : sourceArmed ? DROP_ZONE_TARGET_HOT : DROP_ZONE_IDLE}`}
            >
              {isOver
                ? t('Drop here')
                : sourceArmed
                  ? t('Click to map here')
                  : t('Drag a source field here')}
            </div>
            {/* Unmapped slots can still carry a value via transforms (hardcoded
                            default, fallback field) — the base is null, so only those two
                            kinds are offered until a coalesce provides a real value. */}
            {!readOnly && onChangeTransforms && (
              <TransformAddButton
                onAdd={(tr) => onChangeTransforms([...(transforms ?? []), tr])}
                disabled={transformsLocked}
                allowedKinds={
                  transforms?.some((tr) => tr.kind === 'coalesce')
                    ? undefined
                    : ['default', 'coalesce']
                }
              />
            )}
          </div>
          {transforms && transforms.length > 0 && onChangeTransforms && (
            <TransformPills
              transforms={transforms}
              onChange={onChangeTransforms}
              readOnly={readOnly || transformsLocked}
              previewText={formatPreviewText(transformedValue)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AdditionalRow({
  field,
  index,
  onUpdate,
  onRemove,
  sampleInput,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
  transformsLocked,
}: {
  field: AdditionalField;
  index: number;
  onUpdate: (index: number, field: AdditionalField) => void;
  onRemove: (index: number) => void;
  sampleInput: any;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const pathSegments = field.path ? field.path.split('.') : null;
  const previewValue = pathSegments
    ? valAt(sampleInput, pathSegments)
    : undefined;
  const pathNotInSample = field.path && previewValue === undefined;
  const transformedValue = applyTransformsJs(
    previewValue,
    field.transforms,
    sampleInput,
  );
  const setFieldTransforms = (next: Transform[]) =>
    onUpdate(index, {
      ...field,
      transforms: next.length ? normalizeTransforms(next) : undefined,
    });
  const { t } = useTranslation();

  return (
    <div
      data-slot-row
      tabIndex={-1}
      className="mb-1.5 flex items-start gap-2 rounded focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ui-cta-primary"
    >
      <input
        value={field.name}
        onChange={(e) => onUpdate(index, { ...field, name: e.target.value })}
        placeholder={t('field name')}
        disabled={readOnly}
        className={`${inputClasses.standard} w-[130px] shrink-0 font-mono text-xs`}
      />
      <span className="shrink-0 pt-1.5 text-ui-text-primary-text">←</span>
      {field.path ? (
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Wand outside the chip, always the last icon — mirrors DropSlot. */}
          <div className="flex items-center gap-1">
            <div
              className={`${pathNotInSample ? MAPPING_DISPLAY_WARNING : MAPPING_DISPLAY} min-w-0 flex-1 overflow-hidden`}
            >
              <span
                className="min-w-0 flex-1 truncate"
                style={{ direction: 'rtl', textAlign: 'left' }}
                title={jqPath(field.path.split('.'))}
              >
                <bdi>{jqPath(field.path.split('.'))}</bdi>
              </span>
              {pathNotInSample ? (
                <span className="whitespace-nowrap text-[10px] italic">
                  {t('Not in schema')}
                </span>
              ) : (
                <span className="max-w-[90px] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-ui-text-primary-text">
                  = {JSON.stringify(previewValue)}
                </span>
              )}
              {/* × inside the box removes the ENTIRE field row (name + mapping +
                                transforms) — additional fields are user-added units, so there's
                                no "unmap but keep the slot" like core has. */}
              {!readOnly && (
                <button
                  onClick={() => onRemove(index)}
                  className="text-base leading-none text-unlisted-orange hover:text-ui-status-error-destructive"
                >
                  ×
                </button>
              )}
            </div>
            {!readOnly && (
              <TransformAddButton
                onAdd={(tr) =>
                  setFieldTransforms([...(field.transforms ?? []), tr])
                }
                disabled={transformsLocked}
              />
            )}
          </div>
          {field.transforms && field.transforms.length > 0 && (
            <TransformPills
              transforms={field.transforms}
              onChange={setFieldTransforms}
              readOnly={readOnly || transformsLocked}
              previewText={formatPreviewText(transformedValue)}
            />
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-1">
            <div
              {...handlers}
              tabIndex={readOnly ? undefined : 0}
              role={readOnly ? undefined : 'button'}
              aria-label={
                readOnly
                  ? undefined
                  : sourceArmed
                    ? t('Click to map here')
                    : t('Drag source field')
              }
              onKeyDown={
                readOnly
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStartPick?.(
                          (p) =>
                            onUpdate(index, { ...field, path: p.join('.') }),
                          e.currentTarget,
                        );
                      }
                    }
              }
              onClick={
                readOnly
                  ? undefined
                  : (e) =>
                      onStartPick?.(
                        (p) => onUpdate(index, { ...field, path: p.join('.') }),
                        e.currentTarget,
                      )
              }
              onDrop={
                readOnly
                  ? undefined
                  : (e) => {
                      e.preventDefault();
                      resetOver();
                      if (dragPathRef.current)
                        onUpdate(index, {
                          ...field,
                          path: dragPathRef.current.join('.'),
                        });
                    }
              }
              className={`${DROP_ZONE} focus:outline-none focus:ring-2 focus:ring-ui-cta-primary ${isOver ? DROP_ZONE_ACTIVE : sourceArmed ? DROP_ZONE_TARGET_HOT : DROP_ZONE_IDLE}`}
            >
              <span
                className={`max-w-[90px] flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] ${sourceArmed ? '' : 'text-ui-text-primary-text'}`}
              >
                {isOver
                  ? t('Drop here')
                  : sourceArmed
                    ? t('Click to map here')
                    : t('Drag source field')}
              </span>
            </div>
            {/* Unlike core slots, additional rows are user-added — an unmapped row
                            needs its own × or it can never be removed. */}
            {!readOnly && (
              <HoverTooltip
                content={t('Remove field')}
                position="top"
                focusable={false}
              >
                <button
                  onClick={() => onRemove(index)}
                  className="shrink-0 text-base leading-none text-unlisted-orange hover:text-ui-status-error-destructive"
                  aria-label={t('Remove field')}
                >
                  ×
                </button>
              </HoverTooltip>
            )}
            {/* Path-less additional fields can still carry a value via transforms
                            (hardcoded default, fallback field) — same as empty core slots. */}
            {!readOnly && (
              <TransformAddButton
                onAdd={(tr) =>
                  setFieldTransforms([...(field.transforms ?? []), tr])
                }
                disabled={transformsLocked}
                allowedKinds={
                  field.transforms?.some((tr) => tr.kind === 'coalesce')
                    ? undefined
                    : ['default', 'coalesce']
                }
              />
            )}
          </div>
          {field.transforms && field.transforms.length > 0 && (
            <TransformPills
              transforms={field.transforms}
              onChange={setFieldTransforms}
              readOnly={readOnly || transformsLocked}
              previewText={formatPreviewText(transformedValue)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ConditionRow({
  condition,
  index,
  onUpdate,
  onRemove,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
}: {
  condition: Condition;
  index: number;
  onUpdate: (index: number, condition: Condition) => void;
  onRemove: (index: number) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const { t } = useTranslation();
  const requiresValue = !['not_null', 'is_null'].includes(condition.op);

  return (
    <div
      data-slot-row
      tabIndex={-1}
      className="mb-1.5 flex flex-wrap items-center gap-1.5 rounded focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ui-cta-primary"
    >
      {condition.field ? (
        <div className="flex min-w-0 items-center gap-1 overflow-hidden rounded bg-unlisted-light-blue px-2 py-0.5 font-mono text-xs text-unlisted-dark-blue-800">
          <span
            className="min-w-0 truncate"
            style={{ direction: 'rtl', textAlign: 'left' }}
            title={jqPath(condition.field.split('.'))}
          >
            <bdi>{jqPath(condition.field.split('.'))}</bdi>
          </span>
          {!readOnly && (
            <button
              onClick={() => onUpdate(index, { ...condition, field: '' })}
              className="shrink-0 text-sm leading-none text-ui-text-secondary-text hover:text-ui-text-primary-text"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <div
          {...handlers}
          tabIndex={readOnly ? undefined : 0}
          role={readOnly ? undefined : 'button'}
          aria-label={
            readOnly
              ? undefined
              : sourceArmed
                ? t('Click to map here')
                : t('Drag field')
          }
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStartPick?.(
                      (p) =>
                        onUpdate(index, { ...condition, field: p.join('.') }),
                      e.currentTarget,
                    );
                  }
                }
          }
          onClick={
            readOnly
              ? undefined
              : (e) =>
                  onStartPick?.(
                    (p) =>
                      onUpdate(index, { ...condition, field: p.join('.') }),
                    e.currentTarget,
                  )
          }
          onDrop={
            readOnly
              ? undefined
              : (e) => {
                  e.preventDefault();
                  resetOver();
                  if (dragPathRef.current)
                    onUpdate(index, {
                      ...condition,
                      field: dragPathRef.current.join('.'),
                    });
                }
          }
          className={`min-w-[120px] rounded border-2 border-dashed px-2.5 py-0.5 text-center text-xs transition-all focus:outline-none focus:ring-2 focus:ring-ui-cta-primary ${isOver ? DROP_ZONE_ACTIVE : sourceArmed ? DROP_ZONE_TARGET_HOT : DROP_ZONE_IDLE}`}
        >
          {isOver
            ? t('Drop here')
            : sourceArmed
              ? t('Click to map here')
              : t('Drag field')}
        </div>
      )}
      <SelectMenu
        value={condition.op}
        options={OPS.map((op) => ({
          value: op.v,
          label: t(op.labelKey as any),
        }))}
        onChange={(v) => onUpdate(index, { ...condition, op: v })}
        disabled={readOnly}
        className="w-40"
        compact
        ariaLabel={t('Operator')}
      />
      {requiresValue && (
        <input
          value={condition.value || ''}
          onChange={(e) =>
            onUpdate(index, { ...condition, value: e.target.value })
          }
          placeholder={t('value')}
          disabled={readOnly}
          className={`${inputClasses.standard} w-24 font-mono text-xs`}
        />
      )}
      {!readOnly && (
        <button
          onClick={() => onRemove(index)}
          className="text-lg leading-none text-unlisted-orange hover:text-ui-status-error-destructive"
        >
          ×
        </button>
      )}
    </div>
  );
}

function LockedFieldSlot({
  label,
  expression,
  required,
}: {
  label: string;
  expression: string;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const hasValue = expression.trim().length > 0;
  return (
    <div className="flex items-center gap-2 border-b border-ui-divider-lines py-1.5 last:border-b-0">
      <span
        className="flex items-center gap-1.5 font-mono text-xs font-medium text-ui-text-primary-text"
        style={{ minWidth: 130 }}
      >
        {label}
        {required && (
          <HoverTooltip
            content={t('Required')}
            position="top"
            focusable={false}
            role="img"
            ariaLabel={t('Required')}
          >
            <span className="ml-0.5 font-bold text-unlisted-orange">*</span>
          </HoverTooltip>
        )}
      </span>
      <input
        type="text"
        value={hasValue ? expression : ''}
        placeholder={hasValue ? undefined : t('Not mapped')}
        disabled
        title={hasValue ? expression : undefined}
        className={`${inputClasses.standard} flex-1 bg-unlisted-very-light-gray font-mono text-xs text-ui-text-primary-text`}
      />
      <span className="shrink-0 whitespace-nowrap text-[10px] italic text-ui-text-primary-text">
        {t('Edit in Code mode')}
      </span>
    </div>
  );
}

// ── Main component ──

export type JqFilterBuilderProps = {
  onGenerate: (jq: string) => void;
  onCodeChange?: (jq: string) => void;
  onSampleInputChange?: (sample: object) => void;
  readOnly?: boolean;
  initialJq?: string;
  initialSampleInput?: object;
  objectType?: string;
};

const JqFilterBuilder = ({
  onGenerate,
  onCodeChange,
  onSampleInputChange,
  readOnly = false,
  initialJq,
  initialSampleInput,
  objectType = 'obv',
}: JqFilterBuilderProps) => {
  const { t } = useTranslation();
  const dragPathRef = useRef<string[] | null>(null);
  const jqEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const hasInteracted = useRef(false);
  const codeEditedByUser = useRef(false);
  const codeSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onGenerateRef = useRef(onGenerate);
  onGenerateRef.current = onGenerate;

  // ── Keyboard mapping (a11y) ──
  // Mirrors drag-and-drop for keyboard users: pressing Enter on a drop slot hands its
  // "pick" callback here and moves focus to the first source leaf; pressing Enter on a
  // leaf invokes that callback with the leaf's path. Esc (handled on the tree) cancels.
  const sourceTreeRef = useRef<HTMLDivElement>(null);
  const pendingPickRef = useRef<((path: string[]) => void) | null>(null);
  const lastSlotRef = useRef<HTMLElement | null>(null);
  const [pickActive, setPickActive] = useState(false);

  // Reverse (source-first) pick: clicking a leaf arms its path; the next slot click
  // completes the mapping. Ref mirrors the state so the same-click document listener
  // and startKeyboardPick see completion synchronously, before React re-renders.
  const sourceArmedRef = useRef<string[] | null>(null);
  const [sourceArmedPath, setSourceArmedPath] = useState<string[] | null>(null);

  const cancelSourcePick = useCallback(() => {
    if (!sourceArmedRef.current) return;
    sourceArmedRef.current = null;
    setSourceArmedPath(null);
  }, []);

  const startKeyboardPick = useCallback<StartPick>((onPick, slotEl) => {
    // A source leaf is already armed — complete the mapping right away.
    if (sourceArmedRef.current) {
      const path = sourceArmedRef.current;
      sourceArmedRef.current = null;
      setSourceArmedPath(null);
      onPick(path);
      return;
    }
    const firstLeaf = sourceTreeRef.current?.querySelector<HTMLElement>(
      '[data-leaf-focusable="true"]',
    );
    if (!firstLeaf) return; // no selectable source fields — nothing to pick
    pendingPickRef.current = onPick;
    lastSlotRef.current = slotEl;
    slotEl.classList.add(...ARMED_SLOT_CLASSES);
    setPickActive(true);
    firstLeaf.focus();
  }, []);

  const completeKeyboardPick = useCallback((path: string[]) => {
    const pick = pendingPickRef.current;
    if (!pick) return;
    // The drop zone unmounts once mapped (replaced by the mapping display), so return
    // focus to its persistent row wrapper instead — keeping the user on the right side.
    const slotEl = lastSlotRef.current;
    const row =
      (slotEl?.closest('[data-slot-row]') as HTMLElement | null) ?? slotEl;
    slotEl?.classList.remove(...ARMED_SLOT_CLASSES);
    pendingPickRef.current = null;
    lastSlotRef.current = null;
    setPickActive(false);
    pick(path);
    row?.focus();
  }, []);

  const cancelKeyboardPick = useCallback(() => {
    if (!pendingPickRef.current) return;
    pendingPickRef.current = null;
    setPickActive(false);
    lastSlotRef.current?.classList.remove(...ARMED_SLOT_CLASSES);
    lastSlotRef.current?.focus();
    lastSlotRef.current = null;
  }, []);

  // Leaf activation (Enter or click): completes a slot-first pick if one is pending,
  // otherwise toggles the leaf as the armed source for a source-first pick.
  const activateLeaf = useCallback(
    (path: string[]) => {
      if (pendingPickRef.current) {
        completeKeyboardPick(path);
        return;
      }
      if (sourceArmedRef.current?.join('.') === path.join('.')) {
        cancelSourcePick();
        return;
      }
      sourceArmedRef.current = path;
      setSourceArmedPath(path);
    },
    [completeKeyboardPick, cancelSourcePick],
  );

  // Click-away cancels an active slot-first pick. `mousedown` (not `click`) so that
  // clicking a different empty slot cancels first, then that slot's own click re-arms
  // the pick. Clicks inside the source tree are left to the leaf onClick handlers.
  useEffect(() => {
    if (!pickActive) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (sourceTreeRef.current?.contains(e.target as Node)) return;
      cancelKeyboardPick();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [pickActive, cancelKeyboardPick]);

  // Click-away cancels an armed source leaf. `click` (not `mousedown`) so a slot's own
  // click handler runs first and completes the mapping (clearing the ref) before this
  // fires; the listener is attached after the arming click has already finished.
  useEffect(() => {
    if (!sourceArmedPath) return;
    const onDocClick = (e: MouseEvent) => {
      if (!sourceArmedRef.current) return; // completed by a slot click
      if (sourceTreeRef.current?.contains(e.target as Node)) return; // toggling/re-arming a leaf
      cancelSourcePick();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [sourceArmedPath, cancelSourcePick]);

  // ── Source payload state ──
  const [sampleInput, setSampleInput] = useState<any>(initialSampleInput ?? {});
  const [rawJsonInput, setRawJsonInput] = useState('');
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // ── Builder state (initialized from existing jq if available) ──
  const [parsedInitial] = useState<ParseResult | null>(() =>
    parseJqToBuilderState(initialJq ?? ''),
  );
  const [isComplexJq, setIsComplexJq] = useState(
    () => parsedInitial != null && !parsedInitial.isFullyParseable,
  );
  const [complexFields, setComplexFields] = useState<Record<string, string>>(
    () => parsedInitial?.complexFields ?? {},
  );

  const [coreFieldMaps, setCoreFieldMaps] = useState<CoreFieldMaps>(
    () => parsedInitial?.maps ?? {},
  );
  const [transforms, setTransforms] = useState<TransformMap>(
    () => parsedInitial?.transforms ?? {},
  );
  const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>(
    () => {
      const parsed = parsedInitial?.addl ?? [];
      return parsed.length > 0 ? parsed : [{ name: '', path: '' }];
    },
  );
  const [conditions, setConditions] = useState<Condition[]>(
    () => parsedInitial?.conds ?? [],
  );

  // ── Code editor state (auto-switch to Code for complex jq) ──
  const [isCodeMode, setIsCodeMode] = useState(() => isComplexJq);
  const [codeText, setCodeText] = useState(initialJq ?? '');
  const [codeEditorHeight, setCodeEditorHeight] = useState<number>(
    CODE_EDITOR_MIN_HEIGHT,
  );

  // ── Sync initial sample input from props ──
  useEffect(() => {
    if (initialSampleInput && Object.keys(initialSampleInput).length > 0) {
      setSampleInput(initialSampleInput);
    }
  }, [initialSampleInput]);

  // Core and additional field mappings keep their references when the sample loses a path —
  // DropSlot / AdditionalRow surface a "Not in schema" warning so the user can decide whether
  // to clear them. Conditions don't have an equivalent affordance, so they are still cleared.
  useEffect(() => {
    if (Object.keys(sampleInput).length === 0) return;
    const validPaths = new Set(
      flatPaths(sampleInput).map((p) => p.path.join('.')),
    );

    setConditions((prev) => {
      const next = prev.map((c) =>
        c.field && !validPaths.has(c.field) ? { ...c, field: '' } : c,
      );
      return next.some((c, i) => c !== prev[i]) ? next : prev;
    });
  }, [sampleInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ──
  const generatedJq = useMemo(
    () =>
      genJq(
        conditions,
        coreFieldMaps,
        additionalFields,
        objectType,
        transforms,
      ),
    [conditions, coreFieldMaps, additionalFields, objectType, transforms],
  );
  const evalResult = useMemo(
    () =>
      evalMapping(
        sampleInput,
        coreFieldMaps,
        additionalFields,
        conditions,
        objectType,
        complexFields,
        transforms,
      ),
    [
      sampleInput,
      coreFieldMaps,
      additionalFields,
      conditions,
      objectType,
      complexFields,
      transforms,
    ],
  );
  const outputPreview = evalResult.data ? [evalResult.data] : [];
  const outputErrors = useMemo(() => {
    if (!evalResult.data) return [];
    return validateMappedOutput(evalResult.data, objectType);
  }, [evalResult.data, objectType]);

  // ── Notify parent when jq changes (only after user interaction, never for complex jq) ──
  useEffect(() => {
    if (hasInteracted.current && !isComplexJq)
      onGenerateRef.current(generatedJq);
  }, [generatedJq, isComplexJq]);

  // ── Object type spec & optional fields ──
  const spec = GUNDI_OBJECT_SPECS[objectType];
  const optionalFields = useMemo(
    () => (spec ? spec.fields.filter((f) => !f.required) : []),
    [spec],
  );
  const [activeOptionalKeys, setActiveOptionalKeys] = useState<Set<string>>(
    () => {
      const initial = new Set<string>();
      for (const f of optionalFields) {
        if (coreFieldMaps[f.key] || transforms[f.key]?.length)
          initial.add(f.key);
      }
      return initial;
    },
  );

  // Only prune coreFieldMaps when the user CHANGES objectType, not on initial mount.
  // On initial mount the maps come from the parser and may contain valid fields
  // not in the current spec (e.g. source in an event jq, or title in an observation jq).
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Still seed activeOptionalKeys from initial maps
      const nextActiveOptionalKeys = new Set<string>();
      for (const f of optionalFields) {
        if (coreFieldMaps[f.key] || transforms[f.key]?.length)
          nextActiveOptionalKeys.add(f.key);
      }
      setActiveOptionalKeys(nextActiveOptionalKeys);
      return;
    }

    if (!spec) {
      setActiveOptionalKeys(new Set());
      setCoreFieldMaps(() => ({}) as CoreFieldMaps);
      setTransforms({});
      return;
    }

    // Clear core fields, transforms, and conditions that don't belong to the new object
    // type. Additional fields are NOT touched — they are user-defined and type-independent,
    // and their transforms live on the field objects, so they survive the type change too.
    markInteracted();
    setCoreFieldMaps({} as CoreFieldMaps);
    setTransforms({});
    setConditions([]);
    setActiveOptionalKeys(new Set());
  }, [objectType, spec, optionalFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync code editor when generatedJq changes (e.g. after objectType prune)
  useEffect(() => {
    if (!isComplexJq && !codeEditedByUser.current) setCodeText(generatedJq);
  }, [generatedJq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync builder state from code editor edits (debounced to avoid parsing mid-keystroke)
  useEffect(() => {
    if (!codeEditedByUser.current) return;
    codeEditedByUser.current = false;

    if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    codeSyncTimer.current = setTimeout(() => {
      const parsed = parseJqToBuilderState(codeText);
      if (!parsed) {
        // Empty or fully unparseable — clear builder
        setCoreFieldMaps({} as CoreFieldMaps);
        setTransforms({});
        setAdditionalFields([{ name: '', path: '' }]);
        setConditions([]);
        setComplexFields({});
        setIsComplexJq(false);
        return;
      }
      setCoreFieldMaps(parsed.maps);
      setTransforms(parsed.transforms);
      setConditions(parsed.conds);
      setAdditionalFields(
        parsed.addl.length > 0 ? parsed.addl : [{ name: '', path: '' }],
      );
      setComplexFields(parsed.complexFields);
      setIsComplexJq(!parsed.isFullyParseable);
    }, 400);

    return () => {
      if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    };
  }, [codeText, objectType]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeOptionalFields = optionalFields.filter((f) =>
    activeOptionalKeys.has(f.key),
  );
  const hasMappings =
    Object.values(coreFieldMaps).some((v) => v != null) ||
    additionalFields.some((f) => f.name && (f.path || f.transforms?.length)) ||
    Object.values(transforms).some((chain) => (chain?.length ?? 0) > 0);

  // ── Interaction helpers ──
  const markInteracted = () => {
    hasInteracted.current = true;
  };

  const updateCoreFieldMap = (key: string, path: string[] | null) => {
    markInteracted();
    setCoreFieldMaps((prev) => ({ ...prev, [key]: path }));
    // Clearing a mapping drops any transforms attached to it.
    if (path === null) setTransformsFor(key, []);
  };

  // Set (or clear, when empty) the transforms attached to a core field key.
  const setTransformsFor = (key: string, next: Transform[]) => {
    markInteracted();
    setTransforms((prev) => {
      if (!next.length && !(key in prev)) return prev; // no-op delete — keep identity
      const updated = { ...prev };
      if (next.length) updated[key] = normalizeTransforms(next);
      else delete updated[key];
      return updated;
    });
  };

  const addCondition = () => {
    markInteracted();
    setConditions((prev) => [
      ...prev,
      { field: '', op: 'not_null', value: '' },
    ]);
  };
  const updateCondition = (index: number, updated: Condition) => {
    markInteracted();
    setConditions((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };
  const removeCondition = (index: number) => {
    markInteracted();
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const addAdditionalField = () => {
    markInteracted();
    setAdditionalFields((prev) => [...prev, { name: '', path: '' }]);
  };
  const updateAdditionalField = (index: number, updated: AdditionalField) => {
    markInteracted();
    // Transforms live on the field object, so they travel through renames for free.
    // Only CLEARING a mapped path drops them (mirrors core fields) — a path-less field
    // keeps its transforms, since they are what carries its value.
    setAdditionalFields((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        return f.path && !updated.path
          ? { ...updated, transforms: undefined }
          : updated;
      }),
    );
  };
  const removeAdditionalField = (index: number) => {
    markInteracted();
    setAdditionalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const activateOptionalField = (key: string) => {
    markInteracted();
    setActiveOptionalKeys((prev) => new Set([...prev, key]));
  };
  const deactivateOptionalField = (key: string) => {
    markInteracted();
    setActiveOptionalKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setCoreFieldMaps((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setTransforms((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleCollapsed = useCallback(
    (key: string) =>
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      }),
    [],
  );

  const applyJsonEdit = () => {
    try {
      const parsed = JSON.parse(rawJsonInput);
      setSampleInput(parsed);
      onSampleInputChange?.(parsed);
      setJsonParseError(null);
      setIsEditingJson(false);
    } catch {
      setJsonParseError(t('Invalid JSON'));
    }
  };

  const toggleCodeMode = () => {
    const next = !isCodeMode;
    if (next) setCodeText(isComplexJq ? (initialJq ?? '') : generatedJq);
    setIsCodeMode(next);
  };

  // Complex fields are individually locked via LockedFieldSlot; simple fields remain editable

  // ── Render ──
  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-2 gap-3">
        {/* ── Source Payload Panel ── */}
        <div className={PANEL_CLASSES}>
          <div className={HEADER_CLASSES}>
            <span>{t('Source Payload')}</span>
            {!readOnly && (
              <SegmentedToggle
                value={isEditingJson ? 'edit' : 'view'}
                options={[
                  { value: 'view', label: t('View') },
                  { value: 'edit', label: t('Edit') },
                ]}
                onChange={(v) => {
                  const editing = v === 'edit';
                  setIsEditingJson(editing);
                  if (editing)
                    setRawJsonInput(JSON.stringify(sampleInput, null, 2));
                  else setJsonParseError(null);
                }}
              />
            )}
          </div>
          {isEditingJson ? (
            <div className="flex flex-1 flex-col gap-2 p-2.5">
              <textarea
                value={rawJsonInput}
                onChange={(e) => setRawJsonInput(e.target.value)}
                className={`${inputClasses.standard} min-h-[160px] flex-1 resize-none font-mono text-xs`}
              />
              {jsonParseError && (
                <div className="text-sm text-ui-status-error-destructive">
                  {jsonParseError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsEditingJson(false);
                    setJsonParseError(null);
                  }}
                  className={buttonClasses.cancelSm}
                  type="button"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={applyJsonEdit}
                  className={buttonClasses.lightGreenSm}
                  type="button"
                >
                  {t('Apply')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col" style={{ height: 360 }}>
              <div
                ref={sourceTreeRef}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return;
                  if (pickActive) {
                    e.preventDefault();
                    cancelKeyboardPick();
                  } else if (sourceArmedPath) {
                    e.preventDefault();
                    cancelSourcePick();
                  }
                }}
                className={`flex-1 overflow-y-auto py-1.5 ${pickActive ? 'rounded ring-2 ring-inset ring-ui-cta-primary' : ''}`}
              >
                {Object.keys(sampleInput).length === 0 ? (
                  <div className="px-3.5 py-2 text-xs italic text-ui-text-primary-text">
                    {!readOnly
                      ? t('Click "Edit" to add sample payload')
                      : t('No sample payload')}
                  </div>
                ) : (
                  Object.entries(sampleInput).map(([key, value]) => (
                    <TreeNode
                      key={key}
                      name={key}
                      value={value}
                      path={[key]}
                      depth={0}
                      collapsed={collapsedNodes}
                      onToggle={toggleCollapsed}
                      readOnly={readOnly}
                      dragPathRef={dragPathRef}
                      onLeafActivate={activateLeaf}
                      pickActive={pickActive}
                      armedPathKey={sourceArmedPath?.join('.')}
                    />
                  ))
                )}
              </div>
              {Object.keys(sampleInput).length > 0 && (
                <div className="mt-auto flex items-center gap-1.5 border-t border-ui-divider-lines bg-unlisted-very-light-gray px-3.5 py-2 text-[11px] text-ui-text-primary-text">
                  {pickActive ? (
                    <span className="font-medium text-ui-cta-primary">
                      {t(
                        'Click or press Enter on a field to select it — Esc to cancel',
                      )}
                    </span>
                  ) : sourceArmedPath ? (
                    <span className="font-medium text-ui-cta-primary">
                      {t(
                        'Now click a mapping slot on the right — Esc to cancel',
                      )}
                    </span>
                  ) : (
                    <>
                      <span style={{ color: colors['unlisted-json-key-blue'] }}>
                        ⠿
                      </span>{' '}
                      {t('Drag leaf fields onto mapping slots on the right')}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Field Mappings / Code Editor Panel ── */}
        <div className={PANEL_CLASSES}>
          <div className={HEADER_CLASSES}>
            <span>{t('Field Mappings')}</span>
            <div className="flex items-center gap-2">
              {isCodeMode && (
                <button
                  type="button"
                  data-testid="JqFilterBuilder-copy-button"
                  onClick={() => {
                    copyToClipboard(codeText, t);
                  }}
                  className="flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                  aria-label={t('Copy to clipboard')}
                >
                  <CopyToClipboard />
                </button>
              )}
              <SegmentedToggle
                value={isCodeMode ? 'code' : 'builder'}
                options={[
                  { value: 'code', label: t('Code') },
                  { value: 'builder', label: t('Builder') },
                ]}
                onChange={() => toggleCodeMode()}
              />
            </div>
          </div>
          {isCodeMode ? (
            <>
              {isComplexJq && (
                <div className="border-b border-ui-divider-lines bg-unlisted-light-blue px-3.5 py-2 text-xs text-unlisted-dark-blue-800">
                  {t(
                    'This filter contains complex expressions. Simplify the filter to use the visual Builder.',
                  )}
                </div>
              )}
              {/* Grows with the filter, never shrinks below the size
                                the panel has always had. No resize grip: the box
                                now sizes itself, and a dragged height would be
                                undone by the next keystroke. */}
              <div
                data-testid="JqFilterBuilder-code-editor-box"
                style={{ height: codeEditorHeight, overflow: 'hidden' }}
              >
                <Editor
                  height="100%"
                  language="jq"
                  value={codeText}
                  theme="vs"
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 13,
                    lineHeight: 20,
                    padding: { top: 8 },
                    readOnly,
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    automaticLayout: true,
                    renderLineHighlight: 'none',
                    fontFamily: MONO_FONT_FAMILY,
                  }}
                  beforeMount={registerJqLanguage}
                  onMount={(e) => {
                    jqEditorRef.current = e;
                    const fit = () =>
                      setCodeEditorHeight(
                        Math.max(CODE_EDITOR_MIN_HEIGHT, e.getContentHeight()),
                      );
                    fit();
                    // Fires for typed lines, wrapped lines and
                    // programmatic value changes alike.
                    e.onDidContentSizeChange(fit);
                  }}
                  onChange={(v) => {
                    codeEditedByUser.current = true;
                    setCodeText(v ?? '');
                    onCodeChange?.(v ?? '');
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto px-3.5 py-3">
              {/* Warning banner for complex jq in builder mode */}
              {isComplexJq && (
                <div className="mb-3 rounded-md bg-unlisted-very-light-orange px-3 py-2 text-xs text-unlisted-orange">
                  {t(
                    'Some fields use complex expressions that can only be edited in Code mode.',
                  )}
                </div>
              )}

              {/* Conditions Section */}
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className={SECTION_LABEL}>{t('Conditions')}</span>
                  {!readOnly && (
                    <button
                      onClick={addCondition}
                      className={buttonClasses.lightGreenSm}
                      type="button"
                    >
                      {t('Add Condition')}
                    </button>
                  )}
                </div>
                {conditions.length === 0 && (
                  <div className="text-xs text-ui-text-primary-text">
                    {t('No conditions — all records pass through')}
                  </div>
                )}
                {conditions.map((condition, index) => (
                  <ConditionRow
                    key={index}
                    condition={condition}
                    index={index}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={updateCondition}
                    onRemove={removeCondition}
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                  />
                ))}
              </div>

              {/* Core Fields Section */}
              <div className="mb-4">
                <span className={SECTION_LABEL}>{t('Core Fields')}</span>
                {spec ? (
                  spec.fields
                    .filter((f) => f.required || f.showInCore)
                    .map((f) => {
                      if (complexFields[f.key]) {
                        return (
                          <LockedFieldSlot
                            key={f.key}
                            label={f.labelKey}
                            expression={complexFields[f.key]}
                            required={f.required}
                          />
                        );
                      }
                      return (
                        <DropSlot
                          key={f.key}
                          label={f.labelKey}
                          required={f.required}
                          mapping={coreFieldMaps[f.key] ?? null}
                          sampleInput={sampleInput}
                          readOnly={readOnly}
                          dragPathRef={dragPathRef}
                          onDrop={(p) => updateCoreFieldMap(f.key, p)}
                          onClear={() => updateCoreFieldMap(f.key, null)}
                          onStartPick={startKeyboardPick}
                          sourceArmed={sourceArmedPath != null}
                          transforms={transforms[f.key]}
                          onChangeTransforms={(next) =>
                            setTransformsFor(f.key, next)
                          }
                          transformsLocked={isComplexJq}
                        />
                      );
                    })
                ) : (
                  <div className="text-xs text-ui-text-primary-text">
                    {t('Select an object type to see required fields')}
                  </div>
                )}
              </div>

              {/* Additional Fields Section */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className={SECTION_LABEL}>
                    {t('Additional Fields')}
                  </span>
                  {!readOnly && (
                    <button
                      onClick={addAdditionalField}
                      className={buttonClasses.lightGreenSm}
                      type="button"
                    >
                      {t('Add Field')}
                    </button>
                  )}
                </div>

                {additionalFields.map((field, index) => (
                  <AdditionalRow
                    key={`custom-${index}`}
                    field={field}
                    index={index}
                    sampleInput={sampleInput}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={updateAdditionalField}
                    onRemove={removeAdditionalField}
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                    transformsLocked={isComplexJq}
                  />
                ))}

                {/* Complex additional fields (locked) */}
                {Object.entries(complexFields)
                  .filter(([key]) => key.startsWith('addl:'))
                  .map(([key, expr]) => (
                    <LockedFieldSlot
                      key={key}
                      label={key.slice(5)}
                      expression={expr}
                    />
                  ))}

                {activeOptionalFields.length === 0 &&
                  additionalFields.length === 0 &&
                  !Object.keys(complexFields).some((k) =>
                    k.startsWith('addl:'),
                  ) && (
                    <div className="text-xs text-ui-text-primary-text">
                      {t('No additional fields')}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Output Preview ── */}
      <div className="mt-3 flex flex-col overflow-hidden rounded-lg border border-ui-divider-lines">
        <div className={`${HEADER_CLASSES} flex-shrink-0`}>
          <span>{t('Output Preview')}</span>
          {!hasMappings && (
            <span className="text-xs font-normal text-ui-text-primary-text">
              {t('Map some fields to see output')}
            </span>
          )}
        </div>
        {hasMappings && evalResult.failedConditions.length > 0 ? (
          <div className="bg-unlisted-very-light-orange px-4 py-3 text-sm">
            <p className="mb-1 font-medium text-unlisted-orange">
              {t('Conditions not met — record would be skipped')}
            </p>
            <ul className="list-inside list-disc text-xs text-ui-text-primary-text">
              {evalResult.failedConditions.map((description, index) => (
                <li key={index} className="font-mono">
                  {description}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {hasMappings && outputErrors.length > 0 && (
              <div className="border-b border-ui-divider-lines bg-unlisted-very-light-orange px-4 py-3">
                <p className="mb-1.5 text-xs font-semibold text-unlisted-orange">
                  {t('Validation errors')}
                </p>
                <ul className="flex flex-col gap-1">
                  {outputErrors.map((error, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-1.5 text-xs text-ui-text-primary-text"
                    >
                      <span className="font-mono text-unlisted-orange">
                        {error.field}
                      </span>
                      <span className="text-ui-text-primary-text">—</span>
                      <span>
                        {String(
                          t(
                            error.messageKey as any,
                            error.messageParams as any,
                          ),
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <pre className="m-0 min-h-[60px] whitespace-pre-wrap bg-greyscale-11 p-4 font-mono text-xs leading-relaxed text-ui-text-primary-text">
              {hasMappings ? JSON.stringify(outputPreview, null, 2) : ''}
            </pre>
          </>
        )}
      </div>
    </div>
  );
};

export default JqFilterBuilder;
